import * as React from "react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { ChevronDown, X } from "lucide-react";
import { Checkbox } from "../../../shared/ui/checkbox";
import { cn } from "../../../shared/lib";

export interface DropdownOption {
  label: string;
  value: string;
  subtitle?: string;
}

export interface DropdownGroup {
  key?: string;
  title: string;
  hasAllOption?: boolean;
  options: DropdownOption[];
}

interface MultiSelectDropdownProps {
  options?: DropdownOption[];
  groups?: DropdownGroup[];
  selectedValues: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  label?: string;
  showLabel?: boolean;
  hasAllOption?: boolean;
}

const MultiSelectDropdown = ({
  options = [],
  groups,
  selectedValues,
  onChange,
  placeholder = "Select options",
  label,
  showLabel = true,
  hasAllOption = true,
}: MultiSelectDropdownProps) => {
  const [open, setOpen] = React.useState(false);

  const allOptions = React.useMemo(() => {
    if (groups && groups.length > 0) {
      return groups.flatMap((g) => g.options);
    }
    return options;
  }, [options, groups]);

  const selectedCount = allOptions.filter((opt) =>
    selectedValues.includes(opt.value)
  ).length;

  const isAllSelected =
    allOptions.length > 0 && selectedCount === allOptions.length;

  const isSomeSelected =
    selectedCount > 0 && selectedCount < allOptions.length;

  const handleToggleOption = (value: string) => {
    if (selectedValues.includes(value)) {
      onChange(selectedValues.filter((val) => val !== value));
    } else {
      onChange([...selectedValues, value]);
    }
  };

  const handleToggleAll = () => {
    if (isAllSelected) {
      onChange([]);
    } else {
      onChange(allOptions.map((opt) => opt.value));
    }
  };

  const handleToggleGroupAll = (group: DropdownGroup) => {
    const groupValues = group.options.map((opt) => opt.value);
    const isGroupAllSelected = groupValues.every((val) =>
      selectedValues.includes(val)
    );

    if (isGroupAllSelected) {
      onChange(selectedValues.filter((val) => !groupValues.includes(val)));
    } else {
      const nextValues = Array.from(
        new Set([...selectedValues, ...groupValues])
      );
      onChange(nextValues);
    }
  };

  const handleRemoveValue = (value: string) => {
    onChange(selectedValues.filter((val) => val !== value));
  };

  const headerTitle = label || placeholder;

  return (
    <DropdownMenu.Root open={open} onOpenChange={setOpen}>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          className="flex h-14 w-full items-center justify-between rounded-[8px] bg-[#E9ECF8] px-4 py-1.5 text-left focus:outline-none cursor-pointer transition-colors hover:bg-[#E2E6F5]"
        >
          <div className="flex flex-col justify-center flex-1 min-w-0 pr-2">
            {selectedValues.length > 0 ? (
              <>
                {showLabel && headerTitle && (
                  <span className="text-[11px] sm:text-[12px] font-[500] text-[#6F739B] truncate leading-tight select-none">
                    {headerTitle}
                  </span>
                )}
                <div className="flex items-center gap-1.5 overflow-x-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden flex-nowrap mt-0.5">
                  {selectedValues.map((val) => {
                    const option = allOptions.find((opt) => opt.value === val);
                    const displayLabel = option?.label || val;
                    return (
                      <div
                        key={val}
                        onClick={(e) => e.stopPropagation()}
                        onPointerDown={(e) => e.stopPropagation()}
                        className="flex items-center gap-1 rounded-[12px] bg-[#CAD5E2] px-2.5 py-0.5 text-[12px] sm:text-[13px] font-[400] text-[#2C2F44] shrink-0 max-w-[150px]"
                      >
                        <span className="truncate">{displayLabel}</span>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleRemoveValue(val);
                          }}
                          onPointerDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              e.stopPropagation();
                              handleRemoveValue(val);
                            }
                          }}
                          className="rounded-full p-0.5 hover:bg-black/10 transition-colors shrink-0 cursor-pointer"
                        >
                          <X size={13} className="text-[#555A7B]" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </>
            ) : (
                <>
                  {showLabel && label && label !== placeholder ? (
                    <>
                      <span className="text-[11px] sm:text-[12px] font-[500] text-[#6F739B] truncate leading-tight select-none">
                        {label}
                      </span>
                      <span className="text-[13px] sm:text-[14px] font-[400] text-[#737AA8] truncate mt-0.5">
                        {placeholder}
                      </span>
                  </>
                ) : (
                  <span className="text-[13px] sm:text-[14px] font-[400] text-[#737AA8] truncate">
                    {placeholder}
                  </span>
                )}
              </>
            )}
          </div>
          <ChevronDown size={18} className="text-[#6F739B] shrink-0" />
        </button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="start"
          sideOffset={8}
          className={cn(
            "z-[120] max-h-[280px] min-w-[var(--radix-dropdown-menu-trigger-width)] overflow-y-auto rounded-[14px] border border-[#E1E6FF] bg-white p-2 shadow-[0_16px_36px_rgba(15,20,40,0.12)]"
          )}
        >
          {hasAllOption && allOptions.length > 0 && (
            <DropdownMenu.Item
              onSelect={(e) => e.preventDefault()}
              onClick={handleToggleAll}
              className="flex cursor-pointer items-center gap-3 rounded-[10px] px-3 py-2 outline-none transition-colors hover:bg-[#F5F6FD] focus:bg-[#F5F6FD]"
            >
              <Checkbox
                checked={isAllSelected}
                indeterminate={isSomeSelected}
                onChange={handleToggleAll}
              />
              <div className="flex flex-col text-left">
                <span className="text-[14px] font-[500] text-[#161827]">All</span>
              </div>
            </DropdownMenu.Item>
          )}

          {groups && groups.length > 0 ? (
            groups.map((group, groupIdx) => {
              const groupValues = group.options.map((opt) => opt.value);
              const groupSelectedCount = groupValues.filter((val) =>
                selectedValues.includes(val)
              ).length;
              const isGroupAllSelected =
                groupValues.length > 0 && groupSelectedCount === groupValues.length;
              const isGroupSomeSelected =
                groupSelectedCount > 0 && groupSelectedCount < groupValues.length;

              return (
                <div
                  key={group.key || group.title || groupIdx}
                  className={cn(
                    (groupIdx > 0 || hasAllOption) && "border-t border-[#F0F2FB] mt-1 pt-1"
                  )}
                >
                  <div className="px-3 pt-2 pb-1 text-[12px] font-[500] text-[#8288B4] select-none">
                    {group.title}
                  </div>

                  {group.hasAllOption && group.options.length > 0 && (
                    <DropdownMenu.Item
                      onSelect={(e) => e.preventDefault()}
                      onClick={() => handleToggleGroupAll(group)}
                      className="flex cursor-pointer items-center gap-3 rounded-[10px] px-3 py-2 outline-none transition-colors hover:bg-[#F5F6FD] focus:bg-[#F5F6FD]"
                    >
                      <Checkbox
                        checked={isGroupAllSelected}
                        indeterminate={isGroupSomeSelected}
                        onChange={() => handleToggleGroupAll(group)}
                      />
                      <span className="text-[14px] font-[500] text-[#383B56]">
                        All
                      </span>
                    </DropdownMenu.Item>
                  )}

                  {group.options.map((option) => {
                    const isSelected = selectedValues.includes(option.value);
                    return (
                      <DropdownMenu.Item
                        key={option.value}
                        onSelect={(e) => e.preventDefault()}
                        onClick={() => handleToggleOption(option.value)}
                        className="flex cursor-pointer items-center gap-3 rounded-[10px] px-3 py-2 outline-none transition-colors hover:bg-[#F5F6FD] focus:bg-[#F5F6FD]"
                      >
                        <Checkbox
                          checked={isSelected}
                          onChange={() => handleToggleOption(option.value)}
                        />
                        <div className="flex flex-col text-left">
                          <span className="text-[14px] font-[450] text-[#161827]">
                            {option.label}
                          </span>
                          {option.subtitle && (
                            <span className="text-[12px] font-[400] text-[#6F739B] mt-0.5">
                              {option.subtitle}
                            </span>
                          )}
                        </div>
                      </DropdownMenu.Item>
                    );
                  })}
                </div>
              );
            })
          ) : (
            options.map((option) => {
              const isSelected = selectedValues.includes(option.value);
              return (
                <DropdownMenu.Item
                  key={option.value}
                  onSelect={(e) => e.preventDefault()}
                  onClick={() => handleToggleOption(option.value)}
                  className="flex cursor-pointer items-center gap-3 rounded-[10px] px-3 py-2 outline-none transition-colors hover:bg-[#F5F6FD] focus:bg-[#F5F6FD]"
                >
                  <Checkbox
                    checked={isSelected}
                    onChange={() => handleToggleOption(option.value)}
                  />
                  <div className="flex flex-col text-left">
                    <span className="text-[14px] font-[450] text-[#161827]">
                      {option.label}
                    </span>
                    {option.subtitle && (
                      <span className="text-[12px] font-[400] text-[#6F739B] mt-0.5">
                        {option.subtitle}
                      </span>
                    )}
                  </div>
                </DropdownMenu.Item>
              );
            })
          )}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};

export default MultiSelectDropdown;
